
import React from "react";
import { i18nManager } from "../../../../../../i18n";
import * as ReactRouterDom from "react-router-dom";
import { ApiResponseData, BaseService, HostRole, HostService, IHostManager, IHostManagerEarning, IMember, ITournamentEdition, ITournamentEditionAnalytics, ITournamentTransaction, SanitizedResponse, TournamentService } from "../../../../../../services";
import { Dropdown, FormControl, DateTimeInput, DateTimePickerLayout, DateHelper, DateTimePickerSelectionMode, ChartType, ChartData, Classname, StringHelper } from "@rivtn/noseur";
import { Utils, Schemes } from "../../../../../../utils";
import AnalCard from "../../../../../shared/analytics/AnalCard";
import { ChartManager } from "../../../../../shared/ChartManager";
import RecentTransactions from "../shared/RecentTransactions";
import { Elements } from "../../../../../shared/Elements";

interface TournamentEarningsAnalyticsProps {
    profile: IMember;
    tournamentEdition: ITournamentEdition;
}

function TournamentEarningsAnalytics(props: TournamentEarningsAnalyticsProps) {

    const { profile, tournamentEdition } = props;
    const hostService = HostService.getInstance();
    const tournamentService = TournamentService.getInstance();
    const labels = i18nManager.Labels.host.manage_tournament.analytics;
    const tournamentAnalyticsChartDataState = React.useState<ChartData>();
    const [daysDifference, setDaysDifference] = React.useState<number>(0);
    const [selectedHostManagers, setSelectedHostManagers] = React.useState<IHostManager[]>([]);
    const [tournamentAnalytics, setTournamentAnalytics] = React.useState<ITournamentEditionAnalytics>();
    const [hostManagerEarnings, setHostManagerEarnings] = React.useState<IHostManagerEarning[]>();
    const [searchParams, setSearchParams] = ReactRouterDom.useSearchParams(window.location.search);
    const [recentTransactions, setRecentTransactions] = React.useState<ApiResponseData<ITournamentTransaction>>({});
    const currencySign = i18nManager.getCurrencySign(searchParams.get("curr") ?? "NGN");

    React.useEffect(fetchInformation, []);

    React.useEffect(() => {
        setDaysDifference(DateHelper.getDaysBetweenDates(
            (searchParams.has("from") ? new Date(searchParams.get("from")!) : DateHelper.getStartOfMonth()),
            (searchParams.has("to") ? new Date(searchParams.get("to")!) : new Date())));
    }, [searchParams]);

    return (<div className="earnings analytics">
        <div className="header">
            <div className="l" style={{ display: "flex", alignItems: "center", marginTop: 15 }}>
                <i className="fa fa-arrow-left" style={{ cursor: "pointer"}} onClick={() => window.history.back()} />
                <span className="lined-limit" style={{ fontSize: 16, marginLeft: 10 }}>{labels.earning_anals}</span>
            </div>
            <div className="queries">
                <span className="link lined-limit" style={{ marginTop: 15, marginRight: 15 }}>{labels.export}</span>
                <Dropdown noIcon scheme={Schemes.OMNEHOST} formControlProps={{ style: { marginLeft: 0 }, className: "plan-dd", isFieldset: true, label: i18nManager.Labels.teams.analytics.country_currency, }}
                    selectedOptionIndex={(i18nManager.CountriesAndCurrencies?.findIndex((p) => p?.currency_code === (searchParams.get("curr") ?? "NGN")) ?? 0)}
                    textInputProps={{ style: { paddingLeft: 15 } }}
                    options={i18nManager.CountriesAndCurrencies} optionMap={{
                        value: "currency_code",
                        label: "{label} ({currency_name} - {currency_code})",
                    }} onSelectOption={(option) => {
                        Utils.updateSearchParams("curr", option.currency_code, setSearchParams, fetchInformation);
                    }} />
                <FormControl className="date-range-dd" isFieldset scheme={Schemes.OMNEHOST} label={i18nManager.Labels.teams.analytics.date_range} rightContent={"fa fa-calendar"} style={{ width: 360 }}>
                    <DateTimeInput fill leftLayout={DateTimePickerLayout.FINE_LEFT_LAYOUT} formatDate={(date) => Utils.formatToFineDate4(date)}
                        selectedDates={[(searchParams.has("from") ? new Date(searchParams.get("from")!) : DateHelper.getStartOfMonth()), new Date(searchParams.get("to") ?? Utils.formatToApiDate(new Date(), false, false)!)]}
                        selectionMode={DateTimePickerSelectionMode.RANGE} onSelectDate={(options) => {
                            Utils.updateSearchParamses({
                                "to": Utils.formatToApiDate(options.toDate!, false, false),
                                "from": Utils.formatToApiDate(options.fromDate!, false, false),
                            }, setSearchParams, fetchInformation);
                        }} />
                </FormControl>
            </div>
        </div>
        <div className="container" style={{ marginTop: 10 }}>
            <div className="primary" style={{ marginLeft: 0 }}>
                <div className="anal-cards" style={{ marginTop: 15 }}>
                    <AnalCard anal={tournamentAnalytics?.earnings?.total} currencySign={currencySign} desc={labels.earned_within_x_days} title={labels.total_earning}
                        icon="fas fa-sack-dollar" daysDifference={daysDifference} />
                    <AnalCard anal={tournamentAnalytics?.earnings?.registration} currencySign={currencySign} desc={labels.earned_frm_tour_regs} title={labels.registration_earning}
                        icon="fas fa-sack-dollar" daysDifference={daysDifference} />
                    <AnalCard anal={tournamentAnalytics?.earnings?.merchandise} currencySign={currencySign} desc={labels.earned_frm_merch_sales} title={labels.merch_earnings}
                        icon="fas fa-sack-dollar" daysDifference={daysDifference} />
                    <AnalCard anal={tournamentAnalytics?.earnings?.advertisement} currencySign={currencySign} desc={labels.earned_frm_ads} title={labels.ads_earning}
                        icon="fas fa-sack-dollar" daysDifference={daysDifference} />
                </div>
                <span style={{ marginBottom: 10, marginTop: 15, marginLeft: 10 }}>
                    {!!selectedHostManagers.length && (<>
                        {i18nManager.Labels.teams.analytics.earnings.earning_anals_for} {selectedHostManagers.map((s) => s.member?.username).join(", ")}
                    </>)}
                </span>
                <div className="analytics" style={{ flex: 1 }}>
                    <div className="container" style={{ marginTop: 0 }}>
                        <div className="primary" style={{ marginTop: -10 }}>
                            {<ChartManager type={ChartType.BAR} title={labels.tournament_anal} chartDataState={tournamentAnalyticsChartDataState} />}
                            <RecentTransactions tournamentEdition={tournamentEdition} hideDescription />
                        </div>
                        <div className="secondary" style={{ marginTop: 5 }}>
                            <div className="tt-earners">
                                <span className="t">{labels.top_earning_managers}</span>
                                {!hostManagerEarnings && Elements.LOADING_STATE}
                                <div className="listers">
                                    {hostManagerEarnings?.map((hostManagerEarning) => {
                                        const isOwner = hostManagerEarning.host_manager.role === HostRole.OWNER;
                                        const isYou = hostManagerEarning.host_manager.member?.external_id === profile.external_id && (i18nManager.Labels.host.analytics.earnings.you);
                                        const memberIndex = selectedHostManagers.findIndex((s) => s.external_id === hostManagerEarning.host_manager.external_id);

                                        return (<div key={hostManagerEarning.host_manager.external_id} className={Classname.build("lister", { "active": (memberIndex > -1) })} onClick={() => {
                                            if (memberIndex > -1) {
                                                selectedHostManagers.splice(memberIndex, 1);
                                            } else {
                                                selectedHostManagers.push(hostManagerEarning.host_manager);
                                            }
                                            setSelectedHostManagers([...selectedHostManagers]);
                                        }}>
                                            <img className="bg" alt={hostManagerEarning.host_manager.member?.username} src={hostManagerEarning.host_manager.member?.profile_banner_url} />
                                            <img className="lo" alt={hostManagerEarning.host_manager.member?.username} src={hostManagerEarning.host_manager.passport_picture_url} />
                                            <div className="details">
                                                <span className="m">{currencySign} {StringHelper.toPresentableMoneyValue(hostManagerEarning.amount)}</span>
                                                <span>
                                                    {hostManagerEarning.host_manager.member?.username}{" "}
                                                    {(isYou || isOwner) && `(${isOwner ? i18nManager.Labels.host.analytics.earnings.owner + (isYou ? ", " : "") : ""}${isYou ? i18nManager.Labels.host.analytics.earnings.you : ""})`}
                                                </span>
                                            </div>
                                            <i className="fa fa-circle" style={{
                                                color: ((memberIndex > -1) ? "var(--mainColor)" : "transparent"),
                                                padding: 1, borderRadius: "100%", border: "1px solid var(--mainColor)",
                                            }} />
                                        </div>);
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>);

    function fetchInformation() {
        const params = {
            currency_code: searchParams.get("curr") ?? "NGN",
            to: searchParams.get("to") ?? Utils.formatToApiDate(new Date())!,
            fields: ["earnings.total", "participants.total", "matches.total", "prize_pool.total"],
            from: searchParams.get("from") ?? Utils.formatToApiDate(DateHelper.getStartOfMonth())!,
        };
        setTournamentAnalytics(undefined);
        tournamentService.getTournamentAnalytics(tournamentEdition.external_id, params).then(({ sanitized }: SanitizedResponse<ITournamentEditionAnalytics>) => {
            setTournamentAnalytics(sanitized);
        }).catch(BaseService.reportError);

        tournamentAnalyticsChartDataState[1](undefined);
        tournamentService.queryAnalyticsChartData(tournamentEdition.external_id, params).then(({ apiResponse }: SanitizedResponse<any>) => {
            tournamentAnalyticsChartDataState[1](apiResponse.data!);
        }).catch(BaseService.reportError);

        setHostManagerEarnings(undefined);
        hostService.getHostManagerEarnings(tournamentEdition.tournament.host.external_id, params).then(({ content }: SanitizedResponse<IHostManagerEarning>) => {
            setHostManagerEarnings(content);
        }).catch(BaseService.reportError);
    }

}

export default TournamentEarningsAnalytics;
